import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuthStore } from "@/stores/adminAuthStore";
import { toastError } from "@/shared/lib/toast";
import { AuthTextField, AuthPasswordField } from "@/shared/components/AuthField";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { validateFields } from "@/shared/lib/validators";
import { AuthHeading } from "@/shared/components/AuthHeading";
import { AuthFocusLayout } from "@/shared/layout/AuthFocusLayout";

export function AdminSignInPage() {
  const signIn = useAdminAuthStore((s) => s.signIn);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const fieldErrors = validateFields(
      { email, password },
      {
        email: [
          { required: true, message: "Email is required" },
          { type: "email", message: "Enter a valid email address" },
        ],
        password: [{ required: true, message: "Password is required" }],
      },
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setLoading(true);
    const result = await signIn(email, password);
    setLoading(false);

    if (result.success) {
      navigate("/admin", { replace: true });
      return;
    }

    toastError(result.error ?? "Unable to sign in.");
  };

  return (
    <AuthFocusLayout portalLabel="Admin Portal">
      <AuthHeading title={<>Welcome Back, <span className="font-extrabold tracking-tight">Admin</span></>} subtitle="Sign in with your administrator credentials" className="mb-8" />

      <form onSubmit={handleSubmit}>
        <AuthTextField
          required
          label="Email"
          placeholder="you@example.com"
          value={email}
          onChange={setEmail}
          error={errors.email}
          disabled={loading}
        />

        <AuthPasswordField
          required
          label="Password"
          placeholder="Enter your password"
          value={password}
          onChange={setPassword}
          error={errors.password}
          disabled={loading}
          className="mb-7"
        />

        <AuthSubmitButton loading={loading}>Sign in</AuthSubmitButton>
      </form>

      <p className="!mt-10 text-center text-sm text-[#9CA3AF]">
        Access is restricted to registered administrator accounts.
      </p>
    </AuthFocusLayout>
  );
}
